// Consultation: encounters, vitals, diagnoses, procedures, prescriptions (FR-EMR-*, FR-RX-*).
// Narrative and free text go into the `enc` envelope; codes, numbers and dates stay clear.
import type { AbstractPowerSyncDatabase } from '@powersync/web';
import type { ClinicCrypto } from '@/lib/crypto/clinic-crypto';
import { newId, nowIso } from '@/lib/db/db';

export interface CCtx { db: AbstractPowerSyncDatabase; crypto: ClinicCrypto; clinicId: string; deviceId: string | null; userId: string | null }

export interface Narrative {
  chief_complaint?: string | null;
  history?: string | null;
  examination?: string | null;
  assessment?: string | null;
  plan?: string | null;
  follow_up_note?: string | null;
}

export interface VitalsInput {
  systolic?: number | null; diastolic?: number | null; pulse?: number | null; temp_c?: number | null;
  spo2?: number | null; resp_rate?: number | null; weight_kg?: number | null; height_cm?: number | null;
  glucose_mg_dl?: number | null; notes?: string | null;
}

export interface DiagnosisInput { icd10_code?: string | null; label?: string | null; is_primary?: boolean; certainty?: 'confirmed' | 'provisional' | 'ruled_out' }
export interface ProcedureInput { price_item_id?: string | null; name: string; qty: number; unit_price: number; notes?: string | null }

export interface RxItemInput {
  drug_id?: string | null; drug_name: string;
  dose_qty?: number | null; dose_unit?: string | null; frequency_code?: string | null; route_code?: string | null;
  instruction_codes?: string[]; duration_days?: number | null; quantity: number; buy_outside?: boolean; free_text?: string | null;
}

/** Open (or resume) the consultation for a token. One encounter per token. */
export async function openEncounter(ctx: CCtx, input: { patient_id: string; doctor_id: string; session_id: string | null; token_id: string | null }): Promise<string> {
  if (input.token_id) {
    const existing = await ctx.db.getOptional<{ id: string }>('select id from encounters where token_id = ? and status <> ?', [input.token_id, 'cancelled']);
    if (existing) return existing.id;
  }
  const id = newId(); const ts = nowIso();
  await ctx.db.execute(
    `insert into encounters (id, clinic_id, patient_id, doctor_id, session_id, token_id, encounter_type, started_at, status, key_version, version, created_at, created_by, updated_at, updated_by, device_id)
     values (?, ?, ?, ?, ?, ?, 'new', ?, 'open', ?, 1, ?, ?, ?, ?, ?)`,
    [id, ctx.clinicId, input.patient_id, input.doctor_id, input.session_id, input.token_id, ts, ctx.crypto.active.version, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
  return id;
}

export async function saveNarrative(ctx: CCtx, encounterId: string, n: Narrative, followUpOn: string | null): Promise<void> {
  const enc = await ctx.crypto.encrypt(encounterId, n as Record<string, unknown>);
  await ctx.db.execute('update encounters set enc = ?, key_version = ?, follow_up_on = ?, updated_at = ?, updated_by = ?, device_id = ? where id = ?',
    [enc, ctx.crypto.active.version, followUpOn, nowIso(), ctx.userId, ctx.deviceId, encounterId]);
}

export async function loadNarrative(ctx: CCtx, encounterId: string): Promise<Narrative> {
  const row = await ctx.db.getOptional<{ enc: string | null }>('select enc from encounters where id = ?', [encounterId]);
  if (!row?.enc) return {};
  try { return await ctx.crypto.decrypt<Narrative>(encounterId, row.enc); } catch { return {}; }
}

export async function saveVitals(ctx: CCtx, encounterId: string, patientId: string, v: VitalsInput): Promise<void> {
  const ts = nowIso();
  const existing = await ctx.db.getOptional<{ id: string }>('select id from vitals where encounter_id = ? order by recorded_at limit 1', [encounterId]);
  const id = existing?.id ?? newId();
  const enc = v.notes ? await ctx.crypto.encrypt(id, { notes: v.notes }) : null;
  const bmi = v.weight_kg && v.height_cm ? Math.round((v.weight_kg / ((v.height_cm / 100) ** 2)) * 10) / 10 : null;
  const cols = [v.systolic ?? null, v.diastolic ?? null, v.pulse ?? null, v.temp_c ?? null, v.spo2 ?? null, v.resp_rate ?? null, v.weight_kg ?? null, v.height_cm ?? null, bmi, v.glucose_mg_dl ?? null, enc];
  if (existing) {
    await ctx.db.execute(`update vitals set systolic=?, diastolic=?, pulse=?, temp_c=?, spo2=?, resp_rate=?, weight_kg=?, height_cm=?, bmi=?, glucose_mg_dl=?, enc=?, updated_at=?, updated_by=?, device_id=? where id=?`,
      [...cols, ts, ctx.userId, ctx.deviceId, id]);
  } else {
    await ctx.db.execute(
      `insert into vitals (id, clinic_id, encounter_id, patient_id, recorded_at, systolic, diastolic, pulse, temp_c, spo2, resp_rate, weight_kg, height_cm, bmi, glucose_mg_dl, enc, created_at, created_by, updated_at, updated_by, device_id)
       values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, ctx.clinicId, encounterId, patientId, ts, ...cols, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
  }
}

interface VitalsStored extends Omit<VitalsInput, 'notes'> { id: string; enc: string | null; bmi: number | null }

export async function loadVitals(ctx: CCtx, encounterId: string): Promise<(VitalsInput & { id: string; bmi: number | null }) | null> {
  const r = await ctx.db.getOptional<VitalsStored>('select * from vitals where encounter_id = ? order by recorded_at limit 1', [encounterId]);
  if (!r) return null;
  let notes: string | null = null;
  if (r.enc) { try { notes = (await ctx.crypto.decrypt<{ notes: string }>(r.id, r.enc)).notes ?? null; } catch { /* other key */ } }
  return { ...r, notes };
}

/** Diagnoses are replaced as a set each save. */
export async function saveDiagnoses(ctx: CCtx, encounterId: string, list: DiagnosisInput[]): Promise<void> {
  const ts = nowIso();
  await ctx.db.writeTransaction(async (tx) => {
    await tx.execute('delete from encounter_diagnoses where encounter_id = ?', [encounterId]);
    for (const [i, d] of list.entries()) {
      const id = newId();
      const enc = d.label ? await ctx.crypto.encrypt(id, { label: d.label }) : null;
      await tx.execute(
        `insert into encounter_diagnoses (id, clinic_id, encounter_id, icd10_code, enc, is_primary, certainty, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, ctx.clinicId, encounterId, d.icd10_code ?? null, enc, (d.is_primary ?? i === 0) ? 1 : 0, d.certainty ?? 'confirmed', ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    }
  });
}

export interface DiagnosisRow { id: string; icd10_code: string | null; label: string | null; is_primary: number; certainty: string; title_en: string | null; title_my: string | null }

export async function loadDiagnoses(ctx: CCtx, encounterId: string): Promise<DiagnosisRow[]> {
  const rows = await ctx.db.getAll<DiagnosisRow & { enc: string | null }>(
    `select d.id, d.icd10_code, d.enc, d.is_primary, d.certainty, i.title_en, i.title_my
     from encounter_diagnoses d left join icd10_codes i on i.code = d.icd10_code where d.encounter_id = ? order by d.is_primary desc`, [encounterId]);
  const out: DiagnosisRow[] = [];
  for (const r of rows) {
    let label: string | null = null;
    if (r.enc) { try { label = (await ctx.crypto.decrypt<{ label: string }>(r.id, r.enc)).label ?? null; } catch { /* other key */ } }
    out.push({ ...r, label });
  }
  return out;
}

export async function saveProcedures(ctx: CCtx, encounterId: string, list: ProcedureInput[]): Promise<void> {
  const ts = nowIso();
  await ctx.db.writeTransaction(async (tx) => {
    await tx.execute('delete from encounter_procedures where encounter_id = ?', [encounterId]);
    for (const p of list) {
      const id = newId();
      const enc = p.notes ? await ctx.crypto.encrypt(id, { notes: p.notes }) : null;
      await tx.execute(
        `insert into encounter_procedures (id, clinic_id, encounter_id, price_item_id, name, qty, unit_price, performed_by, performed_at, enc, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, ctx.clinicId, encounterId, p.price_item_id ?? null, p.name, p.qty, p.unit_price, ctx.userId, ts, enc, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    }
  });
}

export async function loadProcedures(ctx: CCtx, encounterId: string) {
  return ctx.db.getAll<{ id: string; price_item_id: string | null; name: string; qty: number; unit_price: number }>(
    'select id, price_item_id, name, qty, unit_price from encounter_procedures where encounter_id = ?', [encounterId]);
}

/** The prescription for this encounter, created on first use. */
export async function ensurePrescription(ctx: CCtx, encounterId: string, patientId: string, doctorId: string): Promise<string> {
  const existing = await ctx.db.getOptional<{ id: string }>(`select id from prescriptions where encounter_id = ? and status <> 'cancelled'`, [encounterId]);
  if (existing) return existing.id;
  const id = newId(); const ts = nowIso();
  await ctx.db.execute(
    `insert into prescriptions (id, clinic_id, encounter_id, patient_id, doctor_id, status, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, 'draft', ?, ?, ?, ?, ?)`,
    [id, ctx.clinicId, encounterId, patientId, doctorId, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
  return id;
}

export async function saveRxItems(ctx: CCtx, prescriptionId: string, items: RxItemInput[]): Promise<void> {
  const ts = nowIso();
  await ctx.db.writeTransaction(async (tx) => {
    await tx.execute('delete from prescription_items where prescription_id = ?', [prescriptionId]);
    for (const [i, it] of items.entries()) {
      const id = newId();
      const enc = it.free_text ? await ctx.crypto.encrypt(id, { free_text: it.free_text }) : null;
      await tx.execute(
        `insert into prescription_items (id, clinic_id, prescription_id, drug_id, drug_name, dose_qty, dose_unit, frequency_code, route_code, instruction_codes, duration_days, quantity, buy_outside, enc, sort_order, created_at, created_by, updated_at, updated_by, device_id)
         values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, ctx.clinicId, prescriptionId, it.drug_id ?? null, it.drug_name, it.dose_qty ?? null, it.dose_unit ?? null, it.frequency_code ?? null, it.route_code ?? null,
         JSON.stringify(it.instruction_codes ?? []), it.duration_days ?? null, it.quantity, it.buy_outside ? 1 : 0, enc, i, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    }
  });
}

export interface RxItemRow extends RxItemInput { id: string; sort_order: number }

/** As stored in SQLite: booleans are 0/1 and arrays are JSON text. */
interface RxItemStored {
  id: string; drug_id: string | null; drug_name: string; dose_qty: number | null; dose_unit: string | null;
  frequency_code: string | null; route_code: string | null; instruction_codes: string | null;
  duration_days: number | null; quantity: number; buy_outside: number; enc: string | null; sort_order: number;
}

export async function loadRxItems(ctx: CCtx, prescriptionId: string): Promise<RxItemRow[]> {
  const rows = await ctx.db.getAll<RxItemStored>('select * from prescription_items where prescription_id = ? order by sort_order', [prescriptionId]);
  const out: RxItemRow[] = [];
  for (const r of rows) {
    let free_text: string | null = null;
    if (r.enc) { try { free_text = (await ctx.crypto.decrypt<{ free_text: string }>(r.id, r.enc)).free_text ?? null; } catch { /* other key */ } }
    out.push({
      id: r.id, sort_order: r.sort_order, drug_id: r.drug_id, drug_name: r.drug_name,
      dose_qty: r.dose_qty, dose_unit: r.dose_unit, frequency_code: r.frequency_code, route_code: r.route_code,
      instruction_codes: JSON.parse(r.instruction_codes ?? '[]') as string[],
      duration_days: r.duration_days, quantity: r.quantity, buy_outside: r.buy_outside === 1, free_text,
    });
  }
  return out;
}

/** Sign the encounter and its prescription. After this, dispensing can start. */
export async function signEncounter(ctx: CCtx, encounterId: string, prescriptionId: string | null): Promise<void> {
  const ts = nowIso();
  await ctx.db.writeTransaction(async (tx) => {
    await tx.execute(`update encounters set status = 'signed', signed_at = ?, signed_by = ?, ended_at = ?, updated_at = ?, updated_by = ?, device_id = ? where id = ?`,
      [ts, ctx.userId, ts, ts, ctx.userId, ctx.deviceId, encounterId]);
    if (prescriptionId) {
      const n = await tx.getOptional<{ n: number }>('select count(*) as n from prescription_items where prescription_id = ?', [prescriptionId]);
      if ((n?.n ?? 0) > 0) {
        await tx.execute(`update prescriptions set status = 'signed', signed_at = ?, updated_at = ?, updated_by = ? where id = ?`, [ts, ts, ctx.userId, prescriptionId]);
      } else {
        await tx.execute(`update prescriptions set status = 'cancelled', updated_at = ?, updated_by = ? where id = ?`, [ts, ctx.userId, prescriptionId]);
      }
    }
  });
}

/** Quantity from dose × times per day × days, rounded up. */
export function computeQuantity(doseQty: number | null | undefined, timesPerDay: number | null | undefined, days: number | null | undefined): number | null {
  if (!doseQty || !timesPerDay || !days) return null;
  return Math.ceil(doseQty * timesPerDay * days);
}

/** Allergy check by generic name (FR-RX-5). */
export async function allergyConflicts(ctx: CCtx, patientId: string, genericNames: string[]): Promise<string[]> {
  if (genericNames.length === 0) return [];
  const rows = await ctx.db.getAll<{ generic_name: string | null }>('select generic_name from patient_allergies where patient_id = ? and active = 1 and generic_name is not null', [patientId]);
  const allergies = rows.map((r) => (r.generic_name ?? '').toLowerCase()).filter(Boolean);
  return genericNames.filter((g) => allergies.some((a) => g.toLowerCase().includes(a) || a.includes(g.toLowerCase())));
}
